import type { ServerLog } from '../types';

/**
 * Generate a simple UUID v4
 */
export function generateUUID(): string {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

/**
 * Map Pino log level to ServerLogLevel
 */
export function mapPinoLevelToServerLogLevel(
  pinoLevel: number | string
): 'fatal' | 'error' | 'warn' | 'log' | 'debug' | 'verbose' {
  if (typeof pinoLevel === 'string') {
    const lower = pinoLevel.toLowerCase();
    if (lower === 'fatal') return 'fatal';
    if (lower === 'error') return 'error';
    if (lower === 'warn' || lower === 'warning') return 'warn';
    if (lower === 'info' || lower === 'log') return 'log';
    if (lower === 'debug') return 'debug';
    if (lower === 'trace' || lower === 'verbose') return 'verbose';
    return 'log';
  }

  // Pino numeric levels
  if (pinoLevel >= 60) return 'fatal';
  if (pinoLevel >= 50) return 'error';
  if (pinoLevel >= 40) return 'warn';
  if (pinoLevel >= 30) return 'log';
  if (pinoLevel >= 20) return 'debug';
  return 'verbose';
}

/**
 * Extract log level from text content
 */
export function extractLogLevel(text: string): 'fatal' | 'error' | 'warn' | 'log' | 'debug' | 'verbose' {
  const lower = text.toLowerCase();

  if (lower.includes('fatal') || lower.includes('critical')) return 'fatal';
  if (lower.includes('error') || lower.includes('<e>') || lower.includes('✖')) return 'error';
  if (lower.includes('warn') || lower.includes('warning') || lower.includes('<w>') || lower.includes('⚠')) return 'warn';
  if (lower.includes('debug') || lower.includes('<d>')) return 'debug';
  if (lower.includes('verbose') || lower.includes('trace')) return 'verbose';

  return 'log';
}

/**
 * Parse Pino JSON log to ServerLog format
 */
export function parsePinoLog(line: string, source: 'server' | 'trace'): ServerLog | null {
  try {
    const pinoLog = JSON.parse(line);
    const id = generateUUID();

    return {
      id,
      level: mapPinoLevelToServerLogLevel(pinoLog.level),
      timestamp: new Date(pinoLog.time).getTime(),
      message: pinoLog.message || pinoLog.msg || '',
      context: pinoLog.context || null,
      traceId: pinoLog.trace_id || null,
      userId: pinoLog.user_id || null,
      appId: pinoLog.app_id || null,
      tenantId: pinoLog.tenant_id || null,
      stack: pinoLog.stack || null,
      meta: {
        pid: pinoLog.pid,
        hostname: pinoLog.hostname,
        path: pinoLog.path,
        method: pinoLog.method,
        statusCode: pinoLog.status_code,
        durationMs: pinoLog.duration_ms,
        ip: pinoLog.ip,
        pageRoute: pinoLog.page_route,
        requestBody: pinoLog.request_body,
        responseBody: pinoLog.response_body,
        queryParams: pinoLog.query_params,
      },
      tags: [source],
    };
  } catch (error) {
    return null;
  }
}

/**
 * Parse plain text log to ServerLog format
 * Format: [2025-11-20 21:48:42] [server] content
 */
export function parseStdLog(line: string, source: 'server-std' | 'client-std'): ServerLog | null {
  const id = generateUUID();
  const match = line.match(/^\[(\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2})\] \[(server|client)\] (.*)$/);

  if (!match) {
    return {
      id,
      level: 'log',
      timestamp: Date.now(),
      message: line,
      context: null,
      traceId: null,
      userId: null,
      appId: null,
      tenantId: null,
      stack: null,
      meta: null,
      tags: [source],
    };
  }

  const [, timeStr, , content] = match;

  let timestamp: number;
  try {
    const isoStr = timeStr.replace(' ', 'T');
    timestamp = new Date(isoStr).getTime();
    if (isNaN(timestamp)) {
      timestamp = Date.now();
    }
  } catch (error) {
    timestamp = Date.now();
  }

  const level = extractLogLevel(content);

  return {
    id,
    level,
    timestamp,
    message: content,
    context: null,
    traceId: null,
    userId: null,
    appId: null,
    tenantId: null,
    stack: null,
    meta: null,
    tags: [source],
  };
}
